import { Trans } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { z } from 'zod';
import { Mag } from '../components/mag.js';
import { SlotDrawer } from '../components/slot-drawer.js';
import { TopNav } from '../components/top-nav.js';
import type { Brand, BrandCalendarDTO, CalendarChipDTO } from '../lib/api.js';
import { useBrandTitle } from '../lib/document-title.js';
import {
  brandCalendarQuery,
  brandChannelDetailQuery,
  brandsQuery,
  meQuery,
} from '../lib/queries.js';

// /brand/$slug/calendar — unified 14-day editorial calendar
// (the agency coordination roadmap (CommitMind) C4).
//
// One surface, three channels: organic_meta slots + meta paid
// launches + google paid launches stacked per day. This is the
// "agency lead's shared editorial calendar" — without it, the
// product feels like three uncoordinated agents instead of one.
//
// Pure UI + one read endpoint (GET /api/brands/:id/calendar).
// No new agent, no schema, no migration.

// s216-54h.62 — slot drawer opens in-place on the calendar via two
// search params: ?slot=<uuid>&slotKind=<channelKind>. clicking a
// draft chip swaps the URL on the calendar page itself (no navigate
// away); the drawer mounts over the timeline; close clears both
// params and the founder is back where they were.
const calendarChannelKind = z.enum(['instagram', 'linkedin', 'tiktok', 'youtube']);
const calendarSearchSchema = z.object({
  slot: z.string().uuid().optional(),
  slotKind: calendarChannelKind.optional(),
});

export const Route = createFileRoute('/brand/$slug/calendar')({
  validateSearch: (s) => calendarSearchSchema.parse(s),
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const brands = await queryClient.ensureQueryData(brandsQuery());
    const brand = brands.find((b) => b.slug === params.slug);
    if (!brand) throw redirect({ to: '/brands' });
    const calendar = await queryClient.ensureQueryData(brandCalendarQuery(brand.id));
    return { brand, calendar };
  },
  loader: ({ context }) =>
    context as {
      brand: Brand;
      calendar: BrandCalendarDTO | null;
    },
  component: CalendarPage,
});

function CalendarPage() {
  const { brand, calendar } = Route.useLoaderData();
  const search = Route.useSearch();
  useBrandTitle(brand.slug, ['calendar']);

  const totalChips = useMemo(
    () => calendar?.days.reduce((sum, d) => sum + d.chips.length, 0) ?? 0,
    [calendar],
  );

  return (
    <main className="min-h-screen bg-[var(--cream)]">
      {/* s216-54h.62 — in-place slot drawer. fetches channel detail
          only when both ?slot + ?slotKind are set; otherwise the
          query is disabled so the page doesn't burn requests on
          every load. */}
      {search.slot && search.slotKind ? (
        <CalendarSlotDrawer brand={brand} slotId={search.slot} channelKind={search.slotKind} />
      ) : null}
      <TopNav active={null} />
      <section className="mx-auto max-w-[840px] px-4 pb-16 pt-6 sm:px-7">
        {/* Back to brand home. Mirrors the same ← link the new-
            campaign flow uses so the founder learns one pattern for
            "step out of this surface." */}
        <Link
          to="/brand/$slug/home"
          params={{ slug: brand.slug }}
          className="inline-block font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--ink)]/55 no-underline transition hover:text-[var(--ink)]"
        >
          {'←'} <Trans>back to home</Trans>
        </Link>
        <header className="mb-6 mt-4">
          <p className="font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-[var(--plum)]">
            {'↘'} <Trans>calendar</Trans>
          </p>
          <h1 className="mt-2 font-display text-[28px] font-extrabold leading-tight tracking-[-0.02em] text-[var(--ink)] sm:text-[34px]">
            <Trans>the next two weeks</Trans>
          </h1>
          <p className="mt-2 max-w-[640px] text-[14px] leading-[1.55] text-[var(--ink)]/65">
            {totalChips === 0 ? (
              <Trans>
                nothing scheduled across organic or paid yet — once campaigns are live, every post
                and ad launch lands here so we both see the same calendar.
              </Trans>
            ) : (
              <Trans>
                everything that's coming up across organic + paid, in one place. click a chip to
                open the campaign.
              </Trans>
            )}
          </p>
        </header>

        {calendar ? (
          <>
            {calendar.monthlyIntent ? (
              <MonthlyIntentBanner intent={calendar.monthlyIntent} />
            ) : null}
            {totalChips === 0 ? (
              <EmptyState />
            ) : (
              <CalendarTimeline brand={brand} calendar={calendar} />
            )}
          </>
        ) : (
          <EmptyState />
        )}
      </section>
    </main>
  );
}

/** s216-54h.62 — drawer wrapper for in-place opens on the calendar.
 *  Fetches the channel detail (which carries the full slot + channel
 *  rows), finds the slot by id, and mounts SlotDrawer. On close,
 *  clears ?slot + ?slotKind via replace so the back button doesn't
 *  unwind into the deep-link URL.
 *
 *  Soft loading state: when the channel detail is still in flight or
 *  the slot id doesn't resolve (stale URL after a slot was deleted),
 *  we render nothing rather than a spinner. The calendar timeline
 *  underneath is visible regardless. */
function CalendarSlotDrawer({
  brand,
  slotId,
  channelKind,
}: {
  brand: Brand;
  slotId: string;
  channelKind: 'instagram' | 'linkedin' | 'tiktok' | 'youtube';
}) {
  const navigate = useNavigate();
  const { data } = useQuery({
    ...brandChannelDetailQuery(brand.id, channelKind),
    staleTime: 20_000,
  });
  const slot = data?.slots.find((s) => s.id === slotId) ?? null;
  const channel = data?.channel ?? null;
  const handleClose = () => {
    void navigate({
      to: '/brand/$slug/calendar',
      params: { slug: brand.slug },
      search: {},
      replace: true,
    });
  };
  if (!slot || !channel) return null;
  return (
    <SlotDrawer
      slot={slot}
      brand={brand}
      kind={channelKind}
      channel={channel}
      onClose={handleClose}
    />
  );
}

function MonthlyIntentBanner({
  intent,
}: {
  intent: NonNullable<BrandCalendarDTO['monthlyIntent']>;
}) {
  return (
    <article className="mb-6 rounded-xl border-[1.5px] border-[var(--ink)] bg-[var(--spell-soft)] p-4 shadow-[3px_3px_0_var(--plum)] sm:p-5">
      <header className="mb-2 flex items-baseline justify-between gap-3">
        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--plum)]">
          <Trans>↘ this month's plan · {intent.planMonth}</Trans>
        </p>
      </header>
      {intent.summary ? (
        <p className="font-serif text-[16px] leading-[1.45] text-[var(--ink)]/85">
          {intent.summary}
        </p>
      ) : null}
      {intent.launches.length > 0 || intent.events.length > 0 ? (
        <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 font-mono text-[11px] text-[var(--ink)]/75">
          {intent.launches.map((l) => (
            <li key={`launch:${l.title}`}>
              <span className="font-bold uppercase tracking-[0.06em] text-[var(--pulse)]">
                <Trans>launch</Trans>
              </span>{' '}
              {l.title} · {l.whenIsh}
            </li>
          ))}
          {intent.events.map((e) => (
            <li key={`event:${e.title}`}>
              <span className="font-bold uppercase tracking-[0.06em] text-[var(--plum)]">
                <Trans>event</Trans>
              </span>{' '}
              {e.title} · {e.whenIsh}
            </li>
          ))}
        </ul>
      ) : null}
    </article>
  );
}

// s234 — vertical timeline (was a 7-col grid). The grid worked
// at-a-glance but every day cell was 157px wide and chips were
// truncated to two-line clamps. The mini calendar in the home sidebar
// is the new at-a-glance surface; this page now reads as a planning
// timeline where you can see each day's content in full. Empty days
// collapse to a thin "— quiet —" row so they don't waste rows.
function CalendarTimeline({ brand, calendar }: { brand: Brand; calendar: BrandCalendarDTO }) {
  const todayIso = new Date().toISOString().slice(0, 10);
  return (
    <ol className="flex flex-col gap-2.5">
      {calendar.days.map((day) => {
        const isToday = day.date === todayIso;
        const isPast = day.date < todayIso;
        return (
          <li
            key={day.date}
            // s234 — id matches the mini-calendar deep-link hash
            // (`#date-YYYY-MM-DD`). scroll-mt-20 offsets the sticky
            // topnav so the anchored day isn't tucked under it.
            // The :target selector below adds a soft yellow ring +
            // flash so the founder can see which day they jumped to.
            id={`date-${day.date}`}
            className={`scroll-mt-20 target:animate-[mini-cal-flash_900ms_ease-out] flex gap-3 rounded-xl border bg-white p-3 transition sm:gap-4 sm:p-4 ${
              isToday
                ? 'border-[1.5px] border-[var(--ink)] shadow-[3px_3px_0_var(--plum)]'
                : isPast
                  ? 'border-[var(--line)] opacity-60'
                  : 'border-[var(--line)]'
            }`}
          >
            <DayPill date={day.date} isToday={isToday} />
            <div className="min-w-0 flex-1">
              {day.chips.length === 0 ? (
                <p className="pt-1 font-mono text-[10.5px] uppercase tracking-[0.12em] text-[var(--ink)]/35">
                  <Trans>— quiet —</Trans>
                </p>
              ) : (
                <ol className="flex flex-col gap-2">
                  {day.chips.map((chip) => (
                    <CalendarChip
                      key={`${chip.kind}:${chip.anchorId ?? chip.label}`}
                      chip={chip}
                      brand={brand}
                    />
                  ))}
                </ol>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

const WEEKDAY = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const MONTH = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

// Fixed-width date pill on the left of each timeline row. Stacks
// weekday + big day number + month so all rows align vertically and
// today reads with extra weight (plum bg + cream text).
function DayPill({ date, isToday }: { date: string; isToday: boolean }) {
  const parts = date.split('-');
  const yyyy = Number.parseInt(parts[0] ?? '0', 10);
  const mm = Number.parseInt(parts[1] ?? '0', 10) - 1;
  const dd = Number.parseInt(parts[2] ?? '0', 10);
  const d = new Date(Date.UTC(yyyy, mm, dd));
  const weekday = WEEKDAY[d.getUTCDay()] ?? '?';
  const month = MONTH[mm] ?? '?';
  return (
    <div
      className={`flex w-[60px] shrink-0 flex-col items-center rounded-lg px-2 py-2 ${
        isToday
          ? 'bg-[var(--ink)] text-[var(--cream)]'
          : 'border border-[var(--line)] bg-[var(--paper)] text-[var(--ink)]'
      }`}
    >
      <span
        className={`font-mono text-[9.5px] font-bold uppercase tracking-[0.1em] ${
          isToday ? 'text-[var(--cream)]/75' : 'text-[var(--ink)]/55'
        }`}
      >
        {weekday}
      </span>
      <span className="mt-0.5 font-display text-[22px] font-black leading-none tracking-tight tabular-nums">
        {dd}
      </span>
      <span
        className={`mt-0.5 font-mono text-[9px] uppercase tracking-[0.1em] ${
          isToday ? 'text-[var(--cream)]/65' : 'text-[var(--ink)]/45'
        }`}
      >
        {month}
      </span>
    </div>
  );
}

/** Channel kinds whose organic_slot chips back onto the new
 *  brand_content_slots tables (s216-54h.57 plumbing). For these,
 *  chip.campaignId is actually the brand_channel_id and chip.
 *  anchorId is the slot id — the click should deep-link into the
 *  channel detail's slots view with the slot drawer pre-opened
 *  (s216-54h.61). Legacy organic_meta campaigns continue to use
 *  the campaign link. */
const CHANNEL_KIND_FOR_CALENDAR = new Set(['instagram', 'linkedin', 'tiktok', 'youtube']);

function CalendarChip({ chip, brand }: { chip: CalendarChipDTO; brand: Brand }) {
  const palette = chipPalette(chip);
  const hourLabel =
    chip.kind === 'organic_slot' && typeof chip.hour === 'number'
      ? `${chip.hour.toString().padStart(2, '0')}:00`
      : null;
  const statusLabel =
    chip.status && chip.status !== 'pending' && chip.status !== 'launched'
      ? chip.status.replace(/_/g, ' ')
      : null;
  // s216-54h.61 — split the link target based on whether the chip
  // backs a new-table slot (organic_slot + campaignKind is a channel
  // kind) or a legacy organic_meta / paid campaign. TanStack's Link
  // is union-typed so the two branches need separate JSX, not a
  // computed props object.
  const isNewTableSlot =
    chip.kind === 'organic_slot' &&
    CHANNEL_KIND_FOR_CALENDAR.has(chip.campaignKind) &&
    chip.anchorId !== undefined;
  const linkClassName = 'link-lift block rounded-md border px-3 py-2 no-underline';
  const linkStyle = {
    borderColor: palette.border,
    backgroundColor: palette.bg,
    color: palette.fg,
  };
  const title = chip.detail ?? chip.label;
  const LinkWrapper = isNewTableSlot ? (
    // s216-54h.62 — stay on /calendar and open the slot drawer in-
    // place. previous version navigated to /channels/<kind>; founder
    // asked for the drawer to land here so they don't lose the
    // calendar context.
    <Link
      to="/brand/$slug/calendar"
      params={{ slug: brand.slug }}
      search={{
        slot: chip.anchorId as string,
        slotKind: chip.campaignKind as 'instagram' | 'linkedin' | 'tiktok' | 'youtube',
      }}
      title={title}
      className={linkClassName}
      style={linkStyle}
    >
      <CalendarChipBody
        chip={chip}
        palette={palette}
        hourLabel={hourLabel}
        statusLabel={statusLabel}
      />
    </Link>
  ) : (
    <Link
      to="/brand/$slug/campaigns/$id"
      params={{ slug: brand.slug, id: chip.campaignId }}
      title={title}
      className={linkClassName}
      style={linkStyle}
    >
      <CalendarChipBody
        chip={chip}
        palette={palette}
        hourLabel={hourLabel}
        statusLabel={statusLabel}
      />
    </Link>
  );
  return <li>{LinkWrapper}</li>;
}

function CalendarChipBody({
  chip,
  palette,
  hourLabel,
  statusLabel,
}: {
  chip: CalendarChipDTO;
  palette: { channelLabel: string };
  hourLabel: string | null;
  statusLabel: string | null;
}) {
  return (
    <>
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-mono text-[9.5px] font-bold uppercase tracking-[0.1em] opacity-75">
          {palette.channelLabel}
        </span>
        {hourLabel ? (
          <span className="font-mono text-[9.5px] tabular-nums opacity-65">{hourLabel}</span>
        ) : null}
      </div>
      {/* No line-clamp — the timeline gives each chip enough room
            to render the full label. Long captions wrap naturally. */}
      <p className="mt-1 text-[13px] font-medium leading-[1.35]">{chip.label}</p>
      {chip.detail ? (
        <p className="mt-0.5 text-[11.5px] leading-[1.35] opacity-70">{chip.detail}</p>
      ) : null}
      {statusLabel ? (
        <p className="mt-1 font-mono text-[9.5px] uppercase tracking-[0.1em] opacity-65">
          ↘ {statusLabel}
        </p>
      ) : null}
    </>
  );
}

function chipPalette(chip: CalendarChipDTO): {
  bg: string;
  border: string;
  fg: string;
  channelLabel: string;
} {
  if (chip.channel === 'meta_paid') {
    return {
      bg: 'var(--ink)',
      border: 'var(--ink)',
      fg: 'var(--cream)',
      channelLabel: 'meta paid',
    };
  }
  if (chip.channel === 'google_paid') {
    return {
      bg: 'var(--pulse)',
      border: 'var(--ink)',
      fg: 'var(--cream)',
      channelLabel: 'google',
    };
  }
  return {
    bg: 'var(--spell-soft)',
    border: 'var(--plum)',
    fg: 'var(--ink)',
    channelLabel: chip.format ? `ig ${chip.format}` : 'instagram',
  };
}

function EmptyState() {
  return (
    <div className="rounded-2xl border border-dashed border-[var(--ink)]/25 bg-white p-10 text-center">
      <Mag size={72} mood="smug" />
      <p className="mt-4 font-serif text-[20px] leading-tight text-[var(--ink)]/80">
        <Trans>“quiet calendar. when we launch something, you'll see it here.”</Trans>
      </p>
    </div>
  );
}
